/**
 * Types de notifications.
 *
 * ⚠️ La SOURCE DE VÉRITÉ est la table `notification_type_registry` (migration
 * 20260728210000) : c'est elle que lit la porte d'écriture SQL et l'écran de
 * préférences. Cette union sert au confort de typage côté front ; elle a
 * dérivé une première fois (tous les types `AI_*` émis depuis juillet 2026 en
 * étaient absents, ce qui les privait d'icône et de filtre). Tout nouveau type
 * s'ajoute AU REGISTRE d'abord — l'ajouter ici est facultatif.
 */
export type NotificationType =
  // Features & Roadmap
  | 'FEATURE_STATUS_CHANGE'
  | 'FEATURE_APPROVED'
  | 'FEATURE_REJECTED'
  | 'COMMENT_APPROVED'
  | 'COMMENT_REJECTED'
  | 'NEED_STATUS_CHANGE'
  | 'NEED_ADDRESSED'
  | 'CLIENT_FEATURE_REQUEST_SUBMITTED'
  // Stripe / Paiements
  | 'STRIPE_PAYMENT_FAILED'
  | 'STRIPE_DISPUTE'
  | 'STRIPE_SUBSCRIPTION_NEW'
  | 'STRIPE_SUBSCRIPTION_ENDED'
  | 'STRIPE_TRIAL_ENDING'
  | 'STRIPE_SUBSCRIPTION_UPDATED'
  | 'STRIPE_INVOICE_PAID'
  | 'STRIPE_INVOICE_UPCOMING'
  | 'STRIPE_REFUND'
  | 'STRIPE_OTHER'
  /** Type unique historique, remplacé par les types métier ci-dessus (28/07/2026). */
  | 'STRIPE_EVENT'
  // CRM
  | 'CRM_LEAD_CREATED'
  | 'CRM_OPPORTUNITY_WON'
  | 'CRM_OPPORTUNITY_LOST'
  | 'CRM_TASK_DUE'
  | 'CRM_APPOINTMENT_REMINDER'
  | 'CRM_APPOINTMENT_RESCHEDULED'
  | 'CRM_APPOINTMENT_BOOKED'
  | 'CRM_APPOINTMENT_CANCELLED'
  | 'BOOKING_CONFIRMED'
  | 'ACTION_QUEUE_GENERATED'
  /** Une mission Otto est en pause sur une question posée à l'équipe (workAwaitHuman). */
  | 'MISSION_AWAITING_HUMAN'
  /**
   * « Revue du vendredi » (cron vendredi 15 h, migration 20260930171000) : reports, sorties
   * automatiques et questions sans réponse attendent un verdict. Catégorie crm (icône par
   * catégorie dans la cloche), lien `/admin/travail?vue=revue`.
   */
  | 'FLUX_WEEKLY_REVIEW'
  // Newsletter
  | 'NEWSLETTER_SUBSCRIBED'
  | 'NEWSLETTER_UNSUBSCRIBED'
  // Onboarding
  | 'ONBOARDING_COMPLETE'
  /** Un RDV pré-calé ne correspond plus à son rendez-vous (onboarding-pre-scheduled-watchdog). */
  | 'ONBOARDING_PRE_SCHEDULED_DESYNC'
  // Inbox / Client Platform
  | 'GMAIL_SYNC_NEW_MAILS'
  | 'GMAIL_OAUTH_ALERT'
  | 'CLIENT_CONVERSATION_STARTED'
  | 'CLIENT_CONVERSATION_REPLY'
  | 'CLIENT_CONVERSATION_ESCALATED'
  | 'CLIENT_CONVERSATION_ASSIGNED'
  | 'CLIENT_SURVEY_RESPONSE'
  | 'ANNOUNCEMENT_COMMENT'
  // Support
  /**
   * Historical: nothing emits them since tickets were retired, but older rows of
   * `notifications` still carry them (156 in production on 06/10/2026).
   */
  | 'TICKET_CREATED'
  | 'TICKET_UPDATED'
  // Boucles IA — alertes (une décision est attendue)
  | 'AI_LOOP_ALERT'
  | 'AI_MISSION_WATCHDOG'
  | 'MEETING_CR_ALERT'
  // Boucles IA — récaps (le journal vit dans le Cockpit IA)
  | 'AI_BRAIN_DOCTOR'
  | 'AI_LOOP_INBOX_TRIAGE'
  | 'AI_LOOP_THREAD_INSIGHT'
  | 'AI_LOOP_OTTO_CHIEF_RANK'
  | 'AI_LOOP_SALES_REVIEW'
  | 'AI_LOOP_PREBRIEF'
  | 'AI_LOOP_MARKETING'
  | 'AI_LOOP_BLOG'
  | 'AI_LOOP_RELATIONS_AGENT'
  | 'AI_IDENTITY_RECONCILE'
  | 'AI_META_DISTILL'
  // Système
  | 'SYSTEM_ALERT'
  | 'NEWSLETTER_SENT'
  | 'ONBOARDING_STEP';

// Catégories de notifications
export type NotificationCategory =
  | 'stripe'
  | 'crm'
  | 'support'
  | 'feature'
  | 'system'
  | 'newsletter'
  | 'onboarding'
  | 'inbox'
  | 'marketing'
  | 'surveys';

/**
 * Niveau voulu par un utilisateur pour un type donné.
 *   all  — fil + toast + notification bureau / push
 *   feed — visible dans le fil, jamais interrompant
 *   off  — rien n'est écrit
 */
export type NotificationLevel = 'all' | 'feed' | 'off';

export const NOTIFICATION_LEVEL_LABELS: Record<NotificationLevel, string> = {
  all: 'Tout',
  feed: 'Fil seul',
  off: 'Coupé',
};

export const NOTIFICATION_LEVEL_HINTS: Record<NotificationLevel, string> = {
  all: 'Dans la cloche, avec toast et notification bureau.',
  feed: 'Dans la cloche uniquement — rien ne vous interrompt.',
  off: "N'est plus reçu du tout.",
};

/** Une ligne de `notification_type_registry` — la source de vérité des types. */
export interface NotificationTypeRegistryEntry {
  type: string;
  label: string;
  category: NotificationCategory;
  description: string | null;
  default_level: NotificationLevel;
  sort_order: number;
}

/** Le réglage effectif d'un type pour l'utilisateur courant. */
export interface NotificationTypeSetting extends NotificationTypeRegistryEntry {
  /** Niveau appliqué : préférence explicite si elle existe, sinon `default_level`. */
  level: NotificationLevel;
  /** true si l'utilisateur a explicitement réglé ce type (≠ valeur par défaut). */
  isExplicit: boolean;
}

// Priorité des notifications
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface NotificationTemplate {
  id: string;
  type: NotificationType;
  title: string;
  content: string;
  variables: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserNotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  type_preferences: Partial<Record<NotificationType, boolean>>;
  category_preferences?: Partial<Record<NotificationCategory, boolean>>;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  category?: NotificationCategory;
  title: string;
  content: string;
  icon?: string;
  link?: string;
  priority?: NotificationPriority;
  data: Record<string, unknown>;
  read: boolean;
  created_at: string;
  /**
   * false quand le destinataire a réglé ce type sur « fil seul » : la
   * notification s'affiche dans la cloche mais ne déclenche ni toast, ni
   * notification bureau, ni web push. Décidé à l'écriture côté SQL.
   */
  push_eligible: boolean;
}

export interface NotificationVariables {
  // Features
  featureTitle?: string;
  needTitle?: string;
  newStatus?: string;
  oldStatus?: string;
  comment?: string;
  reason?: string;
  // Stripe
  amount?: string;
  currency?: string;
  customerName?: string;
  customerEmail?: string;
  subscriptionId?: string;
  invoiceId?: string;
  // CRM
  leadName?: string;
  opportunityName?: string;
  taskName?: string;
  appointmentDate?: string;
  // Newsletter
  subscriberEmail?: string;
  subscriberName?: string;
  // General
  [key: string]: string | undefined;
}

// Icônes par catégorie
export const CATEGORY_ICONS: Record<NotificationCategory, string> = {
  stripe: 'credit-card',
  crm: 'users',
  support: 'life-buoy',
  feature: 'star',
  system: 'bell',
  newsletter: 'mail',
  onboarding: 'user-plus',
  inbox: 'message-circle',
  marketing: 'megaphone',
  surveys: 'clipboard-list',
};

// Couleurs par catégorie
export const CATEGORY_COLORS: Record<NotificationCategory, string> = {
  stripe: 'bg-purple-100 text-purple-600',
  crm: 'bg-blue-100 text-[#0066FF]',
  support: 'bg-orange-100 text-orange-600',
  feature: 'bg-emerald-100 text-emerald-600',
  system: 'bg-neutral-100 text-neutral-600',
  newsletter: 'bg-pink-100 text-pink-600',
  onboarding: 'bg-cyan-100 text-cyan-600',
  inbox: 'bg-blue-100 text-blue-600',
  marketing: 'bg-fuchsia-100 text-fuchsia-600',
  surveys: 'bg-amber-100 text-amber-600',
};

// Libellés FR par catégorie — utilisés par le centre de notifications et
// l'écran de préférences, qui doivent nommer les mêmes choses pareil.
export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  stripe: 'Paiements',
  crm: 'CRM',
  support: 'Support',
  feature: 'Fonctionnalités',
  system: 'Système',
  newsletter: 'Newsletter',
  onboarding: 'Onboarding',
  inbox: 'Boîte de réception',
  marketing: 'Marketing',
  surveys: 'Sondages',
};

// Couleurs par priorité
export const PRIORITY_COLORS: Record<NotificationPriority, string> = {
  low: 'border-neutral-200',
  normal: 'border-blue-200',
  high: 'border-orange-300',
  urgent: 'border-red-400 bg-red-50',
};

// Interface pour les événements Stripe
export interface StripeEvent {
  id: string;
  stripe_event_id: string;
  event_type: string;
  api_version?: string;
  livemode: boolean;
  object_type?: string;
  object_id?: string;
  data: Record<string, unknown>;
  previous_attributes?: Record<string, unknown>;
  customer_id?: string;
  customer_email?: string;
  customer_name?: string;
  amount?: number;
  currency?: string;
  processed: boolean;
  processed_at?: string;
  error?: string;
  stripe_created_at?: string;
  created_at: string;
}
